import React, { Fragment, useEffect, useMemo, useState, } from 'react';
import { Button, } from 'reactstrap';
import { isEmpty, groupBy, get, pick, omit, uniqBy, keyBy, mapValues, } from 'lodash';
import { toast, } from 'react-toastify';
import { useCounter, useToggle, } from 'react-use';
import { Link } from 'react-router-dom';
import classnames from 'classnames';
import { jsPDF } from 'jspdf';
import { format as formatDate, addYears, startOfDay, endOfDay, } from 'date-fns';
import numeral from 'numeral';

import firebase, { functions } from '../../firebase';
import { getCollectionData, } from '../../shared/firebase';
import { idWithTenant, } from '../../shared/util';
import { drawProductLabel, drawAmazonProductLabel } from '../../label';
import { fields, statuses, boxSizes, setSizes, } from '../../shared/models/receivingRequest';
import { labelString, nekoToggleValues, } from '../../shared/models/product';
import { fieldDisplayValue, } from '../../shared/util';
import { newId, fullPathWithParams } from '../../util';
import useCollectionSubscription from '../hooks/useCollectionSubscription';
import useDocumentsFetch from '../hooks/useDocumentsFetch';
import useQueryParams from '../hooks/useQueryParams';
import AdminPage from '../hocs/AdminPage';
import ReceivingRequestFormModal from '../modals/ReceivingRequestFormModal';
import UnknownReceivingRequestFormModal from '../modals/UnknownReceivingRequestFormModal';
import QueryInput from '../QueryInput';
import QuerySelector from '../QuerySelector';
import QueryBoolean from '../QueryBoolean';
import DateSelector from '../DateSelector';
import AddButton from '../AddButton';
import ExportButton from '../ExportButton';
import EditButton from '../EditButton';
import ModalButton from '../ModalButton';
import FilesUploadButton from '../FilesUploadButton';
import ProductTagsDisplay from '../ProductTagsDisplay';
import TenantTags from '../TenantTags';

const labelWidth = 52.5;
const labelHeight = 29.7;
const { keys, entries } = Object;
const db = firebase.firestore();
const printBarcode = functions.httpsCallable('printBarcode');
const storageRef = firebase.storage().ref();
const locationsRef = db.collection('locations');
const tenantsRef = db.collection('tenants');
const tagsRef = db.collection('tags');
const receivementDocumentNumberGroupsRef = db.collectionGroup('receivementDocumentNumberGroups');
const receivingRequestsRef = db.collectionGroup('receivingRequests');
const inventoriesRef = db.collection('inventories');
const statusOptions = entries(statuses).map(([k, v]) => ({ label: v.label, value: k }));
const dateTypes = {
  scheduleDate: { label: '入荷予定日', fieldName: 'scheduleDate', },
  receivedDate: { label: '入庫日', fieldName: 'receivedAt', },
};
const dateTypeOptions = entries(dateTypes).map(([k, v]) => ({ label: v.label, value: k }));

export default AdminPage(function AdminReceivingRequests (props) {
  const { location, history } = props;
  const [version, { inc: updateVersion }] = useCounter(0);
  const {
    dateTypes: dateTypesForFilter = [],
    dateStart: dateStartString,
    dateEnd: dateEndString,
    receivingRequestIdQuery,
    itemNameQuery,
    statuses: statusesForFilter = [],
    tenantIds: tenantIdsForFilter = [],
    onlyCreatedBySystem,
    receivementDocumentNumberQuery,
  } = useQueryParams();
  const dateStart = dateStartString && startOfDay(new Date(dateStartString));
  const dateEnd = dateEndString && endOfDay(new Date(dateEndString));
  const isSearching = !isEmpty(receivementDocumentNumberQuery);
  const [dateType] = dateTypesForFilter;
  const [tenantIdForFilter] = tenantIdsForFilter;
  const receivementDocumentNumberGroups = useCollectionSubscription(receivementDocumentNumberGroupsRef);
  const receivementDocumentNumberItems = receivementDocumentNumberGroups.flatMap(_ => entries(omit(_, ['id', 'ref'])).filter(_ => !isEmpty(_[1])).map(([receivingRequestId, receivementDocumentNumbers]) => ({ tenantId: _.ref.parent.parent.id, receivingRequestId, receivementDocumentNumbers })));

  // 絞り込み
  let filteredReceivingRequestsRef = receivingRequestsRef;
  if(!isEmpty(tenantIdForFilter)) {
    filteredReceivingRequestsRef = tenantsRef.doc(tenantIdForFilter).collection('receivingRequests');
  }
  if(!isEmpty(statusesForFilter)) {
    filteredReceivingRequestsRef = filteredReceivingRequestsRef.where('status', 'in', statusesForFilter)
  }
  if(dateType != null && [dateStartString, dateEndString].some(_ => !isEmpty(_))) {
    const { fieldName } = dateTypes[dateType];
    filteredReceivingRequestsRef = filteredReceivingRequestsRef.where(fieldName, '>=', dateStart || addYears(new Date(), -1000));
    filteredReceivingRequestsRef = filteredReceivingRequestsRef.where(fieldName, '<=', dateEnd || addYears(new Date(), 1000));
    filteredReceivingRequestsRef = filteredReceivingRequestsRef.orderBy(fieldName, 'desc');
  } else {
    filteredReceivingRequestsRef = filteredReceivingRequestsRef.orderBy('createdAt', 'desc');
  }
  const filteredFetchedReceivingRequests = useCollectionSubscription(!isSearching && filteredReceivingRequestsRef, [tenantIdForFilter, statusesForFilter.join(''), dateType, dateStartString, dateEndString]);

  // 検索
  const searchedReceivingRequests = useDocumentsFetch(receivementDocumentNumberQuery && receivementDocumentNumberItems.filter(_ => _.receivementDocumentNumbers.some(_ => _.match(receivementDocumentNumberQuery))).slice(0, 100).map(_ => db.collection('tenants').doc(_.tenantId).collection('receivingRequests').doc(_.receivingRequestId)), [receivementDocumentNumberQuery, receivementDocumentNumberGroups]);

  const receivingRequests = useMemo(() => {
    let receivingRequests;
    if(isSearching) {
      receivingRequests = searchedReceivingRequests;
    } else {
      receivingRequests = filteredFetchedReceivingRequests;
    }
    return receivingRequests;
  }, [isSearching, searchedReceivingRequests, filteredFetchedReceivingRequests]);

  const locations = useCollectionSubscription(locationsRef);
  const locationsById = useMemo(() => keyBy(locations, 'id'), [locations]);
  const tenants = useCollectionSubscription(tenantsRef.orderBy('createdAt'));
  const tags = useCollectionSubscription(tagsRef.orderBy('createdAt'));
  const tagsById = keyBy(tags, 'id');
  const tenantsById = useMemo(() => keyBy(tenants, 'id'), [tenants]);
  const tenantOptions = useMemo(() => tenants.map(_ => ({ label: `[${_.id}] ${_.name}`, value: _.id })), [tenants]);
  const products = useDocumentsFetch(uniqBy(receivingRequests.map(rr => rr.items.map(_ => rr.ref.parent.parent.collection('products').doc(_.productId))).flat(), idWithTenant), [searchedReceivingRequests, filteredFetchedReceivingRequests, version]);
  // NOTE: ネコポス指定等の変更を再取得を待たずに反映するための上書き(fullId => 差分)
  const [productOverrides, setProductOverrides] = useState({});
  const productsById = useMemo(() => {
    const fetchedProductsById = keyBy(products.filter(_ => _), _ => idWithTenant(_.ref));
    return mapValues(fetchedProductsById, (product, fullId) => ({ ...product, ...productOverrides[fullId] }));
  }, [products, productOverrides]);
  const receivingRequestItems = useMemo(() => {
    return receivingRequests.map((receivingRequest) => {
      const { id, status, ref: { parent: { parent: { id: tenantId } } }, } = receivingRequest;
      const tenant = tenantsById[tenantId];
      const { color } = statuses[status];
      const tenantProducts = products.filter(_ => _).filter(_ => _.ref.parent.parent.id === tenantId);
      return {
        ...receivingRequest,
        tenantId,
        tenant,
        color,
        tenantProducts,
      };
    });
  }, [receivingRequests, tenantsById, products]);

  const filteredReceivingRequestItems = useMemo(() => {
    let filteredReceivingRequestItems = receivingRequestItems;
    if(onlyCreatedBySystem === '1') {
      filteredReceivingRequestItems = filteredReceivingRequestItems.filter(_ => _.isCreatedBySystem);
    }
    if(receivingRequestIdQuery) {
      filteredReceivingRequestItems = filteredReceivingRequestItems.filter(_ => idWithTenant(_.ref).includes(receivingRequestIdQuery));
    }
    if(itemNameQuery) {
      filteredReceivingRequestItems = filteredReceivingRequestItems.filter((receivingRequestItem) => {
        const { items, tenantId, } = receivingRequestItem;
        return items.some((item) => {
          const { productId, } = item;
          const product = productsById[[tenantId, productId].join('-')];
          return get(product, 'name', '').includes(itemNameQuery);
        });
      });
    }
    return filteredReceivingRequestItems;
  }, [receivingRequestItems, onlyCreatedBySystem, receivingRequestIdQuery, itemNameQuery, productsById]);
  const tableBodyContent = useMemo(() => {
    return filteredReceivingRequestItems.map((receivingRequestItem) => {
      const { id, ref, pendingItems = [], items = [], documentNumber, receivementDocumentNumbers, status, note, adminNote, ref: { parent: { parent: { id: tenantId } } }, files = [], receivingOptionValues = [], checkNote, setNote, setQuantities, viaCancel = false, tenant, color, tenantProducts, createdAt, receivedAt, scheduleDate, isReceivedByUser = false, slipImages = [], janImages = [], isCreatedBySystem = false, } = receivingRequestItem;
      const rowItems = items.length > 0 ? items : [{}];
      const storagePath = `tenant/${get(tenant, 'id')}/receivingRequests/${id}`;
      const processValues = (values) => {
        return {
          ...values,
          productIds: values.items.map(_ => _.productId),
          ...(status !== 'received' && values.status === 'received' && { completedAt: new Date() }),
        };
      };
      const onUploaded = async (uploads) => {
        await Promise.all(uploads.map(async ({ file, path, downloadUrl, }) => {
          await ref.set({
            files: firebase.firestore.FieldValue.arrayUnion({
              uploadedAt: new Date(),
              downloadUrl,
              storagePath: path,
              ...pick(file, ['name', 'type']),
            }),
          }, { merge: true });
        }));
      };
      const onClickDeleteFile = async (file) => {
        if(!window.confirm('本当に削除しますか？')) return;

        try {
          file.storagePath && await storageRef.child(file.storagePath).delete();
          await ref.set({
            files: firebase.firestore.FieldValue.arrayRemove({ ...file, }),
          }, { merge: true });
          toast.success('削除しました');
        } catch(e) {
          console.error(e);
          toast.error('失敗しました');
        }
      };


      return tenant != null && (
        <Fragment key={idWithTenant(ref)}>
          {
            rowItems.map((item, i) => {
              const { productId, quantity, } = item;
              const goodInventoryIds = receivingRequestItem[`goodInventoryIds-${productId}`] || [];
              const ngInventoryIds = receivingRequestItem[`ngInventoryIds-${productId}`] || [];
              const product = productsById[[tenant.id, productId].join('-')];
              const { isNeko = false, } = product || {};
              const onClickNeko = async () => {
                const values = nekoToggleValues(product);
                const suffix = ({ true: 'に指定', false: '指定を解除' })[values.isNeko];
                if(!window.confirm(`本当にネコポス${suffix}しますか？`)) return;

                await product.ref.update(values);
                setProductOverrides(_ => ({ ..._, [idWithTenant(product.ref)]: values }));
                updateVersion();
                toast.success(`ネコポス${suffix}しました`);
              };
              const onClickPrintBarcode = async (ng) => {
                try {
                  const canvas = document.createElement('canvas');
                  ((product.isAmazonProduct && product.fnsku && !ng) ? drawAmazonProductLabel : drawProductLabel)(canvas, labelWidth, labelHeight, product, ng);
                  const imageData = canvas.toDataURL('image/jpeg', 0.2);
                  const doc = new jsPDF({
                    orientation: 'landscape',
                    format: [labelWidth, labelHeight]
                  });
                  doc.addImage(imageData, 'JPEG', 0, 0, labelWidth, labelHeight);
                  const pdfData = doc.output('datauri');

                  // DEV
                  doc.save('hoge.pdf');
                  // return;

                  await printBarcode({
                    type: 'receivementProduct',
                    title: labelString(product),
                    imageData: pdfData,
                  });
                } catch(e) {
                  console.error(e);
                  toast.error('失敗しました');
                }
              };

              return (
                <tr key={productId} className={`table-${color}`}>
                  {
                    i === 0 && (
                      <Fragment>
                        <td rowSpan={rowItems.length} className="text-nowrap">
                          <EditButton className="ml-2" itemRef={ref} processValues={processValues} FormModal={ReceivingRequestFormModal} formProps={{ products: tenantProducts, tenant, admin: true }} />
                          <FilesUploadButton className="ml-2" storagePath={storagePath} onUploaded={onUploaded} />
                          <ModalButton
                            label="在庫"
                            title="在庫"
                            className="ml-2"
                            content={_ => <InventoriesModalContent receivingRequest={receivingRequestItem} tenant={tenant} locationsById={locationsById} />}
                          />
                        </td>
                        <td rowSpan={rowItems.length}>
                          <Link to={`/admin/tenants/${tenant.id}`}>
                            {tenant.name}
                          </Link>
                          <TenantTags tagIds={tenant.tagIds} tagsById={tagsById} />
                        </td>
                        <td rowSpan={rowItems.length}>
                          <div className="d-flex align-items-center gap-1"><div>{tenant.id}-{id}</div>{isCreatedBySystem && <div className="badge badge-warning">倉庫作成分</div>}</div>
                          <div>{formatDate(createdAt.toDate(), 'yyyy/MM/dd')}</div>
                          <div>{scheduleDate ? formatDate(scheduleDate.toDate(), 'yyyy/MM/dd') : '(入荷予定日なし)'}</div>
                          <div>{receivedAt ? formatDate(receivedAt.toDate(), 'yyyy/MM/dd') : '(入庫日なし)'}</div>
                          {viaCancel && <div className="badge badge-secondary">出荷キャンセルによる返送分 {tenant.id}-{receivingRequestItem.shippingRequestId}</div>}
                        </td>
                        <td rowSpan={rowItems.length} className="text-break">{documentNumber}</td>
                        <td rowSpan={rowItems.length} className="text-break">{receivementDocumentNumbers?.join(',')}</td>
                        <td rowSpan={rowItems.length}>
                          {
                            pendingItems.map((pendingItem, i) => {
                              const { boxSize, quantity } = pendingItem;
                              return (
                                <div key={i}>
                                  {boxSizes[boxSize].label} {quantity}個
                                </div>
                              );
                            })
                          }
                        </td>
                      </Fragment>
                    )
                  }
                  <td>
                    {
                      product && (
                        <div>
                          <div>
                            {`${tenant.id}-${product.id}`}
                          </div>
                          <span className={classnames('fas fa-cat cursor-pointer', { 'text-info': isNeko, 'text-muted': !isNeko })} onClick={onClickNeko} />
                          <div className="d-flex gap-1">
                            <Button size="sm" color="success" onClick={onClickPrintBarcode.bind(null, false)}>
                              <span className="fas fa-print" />
                            </Button>
                            <Button size="sm" color="warning" onClick={onClickPrintBarcode.bind(null, true)}>
                              <span className="fas fa-print" />
                            </Button>
                          </div>
                        </div>
                      )
                    }
                  </td>
                  <td>
                    <ProductTagsDisplay product={product} productTags={[]} />
                    <div>{product && product.name}</div>
                  </td>
                  <td>
                    {product?.sku}<br />
                    {product?.fnsku}<br />
                    {product?.asin}
                  </td>
                  <td className="text-right">{quantity}</td>
                  <td className="text-right">{goodInventoryIds.length}</td>
                  <td className="text-right">{ngInventoryIds.length}</td>
                  {
                    i === 0 && (
                      <Fragment>
                        <td rowSpan={rowItems.length}>
                          {statuses[status].label}
                          {isReceivedByUser && <span className="badge badge-warning">ユーザー対応</span>}
                        </td>
                        <td rowSpan={rowItems.length}>
                          {fieldDisplayValue(receivingOptionValues, fields().receivingOptionValues)}
                        </td>
                        <td rowSpan={rowItems.length} style={{ whiteSpace: 'pre-line' }}>
                          {checkNote}
                        </td>
                        <td rowSpan={rowItems.length} style={{ whiteSpace: 'pre-line' }}>
                          {setNote}
                        </td>
                        <td rowSpan={rowItems.length} className="text-right">
                          {
                            entries(setSizes).map(([setSize, { label }]) => {
                              const quantity = setQuantities?.[setSize];
                              return quantity != null && (
                                <div key={setSize}>
                                  {label}: {numeral(quantity).format()}
                                </div>
                              );
                            })
                          }
                        </td>
                        <td rowSpan={rowItems.length} style={{ whiteSpace: 'pre-line' }}>
                          {note}
                        </td>
                        <td rowSpan={rowItems.length} style={{ whiteSpace: 'pre-line' }}>
                          {adminNote}
                        </td>
                        <td rowSpan={rowItems.length}>
                          {
                            files.map((file) => {
                              return (
                                <div className="mt-1">
                                  {file.name}
                                  <a className="ml-2" href={file.downloadUrl} target="_blank">
                                    <span className="fas fa-eye" />
                                  </a>
                                  <a className="ml-2 text-danger cursor-pointer" onClick={onClickDeleteFile.bind(null, file)}>
                                    <span className="fas fa-times" />
                                  </a>
                                </div>
                              );
                            })
                          }
                        </td>
                        <td rowSpan={rowItems.length}>
                          {
                            slipImages.map((file) => {
                              return (
                                <div className="mt-1" key={file.downloadUrl}>
                                  <a href={file.downloadUrl} target="_blank">
                                    <img src={file.downloadUrl}  style={{ maxWidth: 150 }}/>
                                  </a>
                                </div>
                              );
                            })
                          }
                        </td>
                        <td rowSpan={rowItems.length}>
                          {
                            janImages.map((file) => {
                              return (
                                <div className="mt-1">
                                  <a href={file.downloadUrl} target="_blank">
                                    <img src={file.downloadUrl}  style={{ maxWidth: 150 }}/>
                                  </a>
                                </div>
                              );
                            })
                          }
                        </td>
                      </Fragment>
                    )
                  }
                </tr>
              )
            })
          }
        </Fragment>
      );
    })
  }, [filteredReceivingRequestItems]);
  const newItemRef = async (values) => {
    const receivingRequestsRef = tenantsRef.doc(values.tenantId).collection('receivingRequests');
    const { docs: [lastReceivingRequestDoc] } = await receivingRequestsRef.orderBy('createdAt', 'desc').limit(1).get();
    return receivingRequestsRef.doc('W' + newId(lastReceivingRequestDoc));
  };
  const strangeRows = async () => {
    const processingReceivingRequests = await getCollectionData(receivingRequestsRef.where('status', '==', 'processing'));
    const targetReceivingRequests = processingReceivingRequests
      .filter(r => r.items.some(_ => (r[`goodInventoryIds-${_.productId}`]?.length || r[`ngInventoryIds-${_.productId}`]?.length) > 0))
      .filter(r => r.items.some(_ => ((r[`goodInventoryIds-${_.productId}`]?.length || 0) + (r[`ngInventoryIds-${_.productId}`]?.length || 0)) !== _.quantity));
    return entries(groupBy(targetReceivingRequests, _ => _.ref.parent.parent.id)).flatMap(([tenantId, receivingRequests]) => {
      return receivingRequests.map((receivingRequest) => {
        const { id, ref, items, notifiedStrangeAt, } = receivingRequest;
        const diffItems = items.filter(_ => ((receivingRequest[`goodInventoryIds-${_.productId}`]?.length || 0) + (receivingRequest[`ngInventoryIds-${_.productId}`]?.length || 0)) !== _.quantity);
        const productIds = diffItems.map(_ => [tenantId, _.productId].join('-'));
        return {
          tenantId,
          receivingRequestId: idWithTenant(ref),
          productIds: productIds.join('-'),
          notifiedStrangeAt: notifiedStrangeAt && formatDate(notifiedStrangeAt.toDate(), 'yyyy/MM/dd HH:mm:ss'),
        };
      });
    });
  };
  useEffect(() => {
    if(isEmpty(statusesForFilter)) {
      const path = fullPathWithParams({ statuses: ['initial', 'unknown', 'pending', 'resubmitted', 'processing'], }, location);
      history.replace(encodeURI(path));
    }
  }, []);

  return (
    <div className="admin-receivingRequests container-fluid">
      <div className="p-4 bg-white my-4">
        <div className="d-flex justify-content-center mb-3">
          <h4>入荷依頼管理</h4>
        </div>
        <div className="d-flex justify-content-start align-items-end mb-3">
          <div className="d-flex align-items-end gap-1">
            <div className="card" style={{ opacity: isSearching && 0.4 }}>
              <div className="card-header">
                絞り込み
              </div>
              <div className="card-body d-flex flex-column gap-1">
                <div className="d-flex align-items-center flex-wrap gap-1">
                  <div style={{ width: 200 }} >
                    <QuerySelector className="w-100" paramName="dateTypes" options={dateTypeOptions} label="日付種別" />
                  </div>
                  <DateSelector paramName="dateStart" label="開始日" disabled={isSearching} />
                  <DateSelector paramName="dateEnd" label="終了日" disabled={isSearching} invalid={dateStart > dateEnd} />
                </div>
                <div className="flex-fill d-flex">
                  <div style={{ width: 300 }} >
                    <QuerySelector className="w-100" paramName="statuses" isMulti options={statusOptions} label="ステータスで絞込み" />
                  </div>
                  <div style={{ width: 150 }} className="ml-2">
                    <QuerySelector className="w-100" paramName="tenantIds" options={tenantOptions} label="テナントで絞込み" />
                  </div>
                  <div style={{ width: 150 }} className="ml-2">
                    <QueryInput width={null} paramName="receivingRequestIdQuery" label="入荷依頼IDで絞込み" />
                  </div>
                  <div style={{ width: 300 }} className="ml-2">
                    <QueryInput width={null} paramName="itemNameQuery" label="商品名で絞込み" />
                  </div>
                </div>
                <div>
                  <QueryBoolean paramName="onlyCreatedBySystem" label="倉庫作成分のみ" defaultValue={'0'} />
                </div>
              </div>
            </div>
            <div className="card">
              <div className="card-header">
                検索
              </div>
              <div className="card-body">
                <div>
                  <QueryInput paramName="receivementDocumentNumberQuery" label="入荷検品時伝票番号 (結果上限 100件)" />
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="d-flex justify-content-start align-items-end gap-1">
          <ExportButton fileName={`入庫差異.csv`} label="入庫差異確認" rows={strangeRows} />
          <AddButton label="不明入庫" itemRef={newItemRef} FormModal={UnknownReceivingRequestFormModal} validateOnSubmit={_ => _.id !== 'W1' || window.confirm('W1を作ることになります。よろしいですか？')} processValues={_ => ({ ...omit(_, 'tenantId'), items: [], productIds: [], })} formProps={{ values: null, tenants, }} />
        </div>
        <div className="mt-4 overflow-scroll position-relative" style={{ zIndex: 0 }}>
          {
            filteredReceivingRequestItems.length > 0 ? (
              <table className="table sticky-table table-bordered">
                <thead className="thead-light text-center">
                  <tr className="text-nowrap">
                    <th></th>
                    <th style={{ minWidth: 150 }}>会社</th>
                    <th style={{ minWidth: 150 }}>
                      <div>入荷依頼ID</div>
                      <div>入荷依頼日</div>
                      <div>入荷予定日</div>
                      <div>入庫日</div>
                    </th>
                    <th style={{ minWidth: 150 }}>伝票番号</th>
                    <th style={{ minWidth: 150 }}>入荷検品時の伝票番号</th>
                    <th style={{ minWidth: 200 }}>不明・確認在庫</th>
                    <th style={{ minWidth: 150 }}>商品ID</th>
                    <th style={{ minWidth: 300 }}>商品名</th>
                    <th style={{ minWidth: 150 }}>
                      SKU<br />
                      FNSKU<br />
                      ASIN<br />
                    </th>
                    <th style={{ minWidth: 100 }}>入庫数</th>
                    <th style={{ minWidth: 100 }}>実入庫数</th>
                    <th style={{ minWidth: 100 }}>販売不可数</th>
                    <th style={{ minWidth: 100 }}>ステータス</th>
                    <th style={{ minWidth: 150 }}>オプション</th>
                    <th style={{ minWidth: 150 }}>検品依頼内容</th>
                    <th style={{ minWidth: 150 }}>セット内容</th>
                    <th style={{ minWidth: 150 }}>セット件数</th>
                    <th style={{ minWidth: 150 }}>連絡メモ</th>
                    <th style={{ minWidth: 150 }}>管理者メモ</th>
                    <th style={{ minWidth: 200 }}>添付ファイル</th>
                    <th style={{ minWidth: 200 }}>伝票の写真</th>
                    <th style={{ minWidth: 200 }}>JANか型番の写真</th>
                  </tr>
                </thead>
                <tbody>
                  {tableBodyContent}
                </tbody>
              </table>
            ) : (
              <div>
                入荷依頼はありません
              </div>
            )
          }
        </div>
      </div>
    </div>
  );
});

function InventoriesModalContent (props) {
  const { tenant, receivingRequest, locationsById, } = props;
  const inventories = useCollectionSubscription(inventoriesRef.where('tenantId', '==', tenant.id).where('receivingRequestId', '==', receivingRequest.id), [receivingRequest]);
  const inventoriesGroupedByProductId = groupBy(inventories, 'productId');

  return (
    <table className="table table-bordered">
      <thead className="thead-light text-center">
        <tr>
          <th>
            商品ID
          </th>
          <th>
            棚
          </th>
          <th>
            在庫数
          </th>
        </tr>
      </thead>
      <tbody>
        {
          entries(inventoriesGroupedByProductId).map(([productId, inventories]) => {
            const inventoriesGroupedByLocationId = groupBy(inventories, 'locationId');
            return (
              <Fragment key={productId}>
                {
                  entries(inventoriesGroupedByLocationId).map(([_, inventories], i) => {
                    const [{ locationId }] = inventories;
                    const location = locationsById[locationId];
                    const rowSpan = keys(inventoriesGroupedByLocationId).length;

                    return (
                      <tr key={locationId} data-ids={inventories.map(_ => _.id).join(',')}>
                        {
                          i === 0 && (
                            <th rowSpan={rowSpan}>
                              {idWithTenant(tenant.ref.collection('products').doc(productId))}
                            </th>
                          )
                        }
                        <td>
                          {locationId == null ? '(未棚入れ)' : (location && location.name)}
                        </td>
                        <td className="text-right">
                          {inventories.length}
                        </td>
                      </tr>
                    );
                  })
                }
              </Fragment>
            );
          })
        }
      </tbody>
    </table>
  );
}
